/**
 * /operations/attendance/biometric-sync — Session 3B PR 5.
 *
 * Owning agent: Tashghil. Health dashboard for biometric devices.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ActivitySquare,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { BiometricDeviceItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StatusFilter = "all" | "healthy" | "degraded" | "offline";

function _statusToneClass(s: BiometricDeviceItem["sync_status"]): string {
  if (s === "offline") return "bg-rose-100 text-rose-800";
  if (s === "degraded") return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
}

function _healthBarColor(pct: number): string {
  if (pct >= 80) return "bg-emerald-500";
  if (pct >= 50) return "bg-amber-500";
  return "bg-rose-500";
}

export function BiometricSyncLogPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const list = useQuery({
    queryKey: ["session3b-biometric-devices", legalEntityId],
    queryFn: () => session3bApi.listBiometricDevices(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: BiometricDeviceItem[] = list.data?.items ?? [];
  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((r) => r.sync_status === statusFilter);
  }, [items, statusFilter]);

  const counts = useMemo(() => {
    const offline = items.filter((r) => r.sync_status === "offline").length;
    const degraded = items.filter((r) => r.sync_status === "degraded").length;
    return {
      total: items.length,
      healthy: items.filter((r) => r.sync_status === "healthy").length,
      degraded,
      offline,
      attention: degraded,
      critical: offline,
      worst_device: items.find((r) => r.sync_status === "offline") ?? items.find((r) => r.sync_status === "degraded"),
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("attendance.biometric_sync.title", "Biometric Sync Log")}
          subtitle={t(
            "attendance.biometric_sync.subtitle",
            "Health dashboard for biometric devices. Tashghil tracks sync cadence + flags offline hubs that block clock-in.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["all", "healthy", "degraded", "offline"] as StatusFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  statusFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`attendance.biometric_sync.filter.${f}`, f)}
                {f === "offline" && counts.offline > 0 && (
                  <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-semibold text-white">
                    {counts.offline}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "attendance.biometric_sync.cta.sync_all_note",
                  "Triggers manual sync across every active device. Wires to the device control endpoint in a follow-up.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <RefreshCw className="h-4 w-4" />
            {t("attendance.biometric_sync.cta.sync_all", "Sync All Devices Now")}
          </button>
        </div>

        {counts.critical > 0 && counts.worst_device && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<AlertTriangle className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "attendance.biometric_sync.suggestion.offline_headline",
              `${counts.offline} device${counts.offline === 1 ? "" : "s"} offline — clock-in is blocked at that location.`,
            )}
            body={t(
              "attendance.biometric_sync.suggestion.offline_body",
              `${counts.worst_device.device_code} at ${counts.worst_device.location_label} hasn't synced. Check the network at the hub.`,
            )}
            primaryAction={{
              label: t("attendance.biometric_sync.suggestion.open_exceptions", "Open Exception Queue"),
              onClick: () =>
                window.location.assign("/operations/attendance/exception-queue"),
            }}
            suggestionKey={`bio-offline-${counts.worst_device.device_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "attendance.biometric_sync.suggestion.empty_headline",
              "No biometric devices registered for this LE.",
            )}
            body={t(
              "attendance.biometric_sync.suggestion.empty_body",
              "Register devices in /settings/integrations to start tracking sync health.",
            )}
            primaryAction={{
              label: t("attendance.biometric_sync.suggestion.go_settings", "Open integrations"),
              onClick: () => window.location.assign("/settings/integrations"),
            }}
            suggestionKey="bio-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("attendance.biometric_sync.col.device", "Device")}</th>
                  <th className="px-3 py-2">{t("attendance.biometric_sync.col.location", "Location")}</th>
                  <th className="px-3 py-2">{t("attendance.biometric_sync.col.last_sync", "Last sync")}</th>
                  <th className="px-3 py-2">{t("attendance.biometric_sync.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("attendance.biometric_sync.col.pending", "Pending records")}</th>
                  <th className="px-3 py-2">{t("attendance.biometric_sync.col.health", "Health score")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.device_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2 font-mono text-xs">{row.device_code}</td>
                    <td className="px-3 py-2 text-text-secondary">{row.location_label}</td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.last_sync_at ?? "—"}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.sync_status)}`}
                      >
                        {row.sync_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.pending_records}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "attendance.biometric_sync.trace.health",
                              `Health score = composite of recent sync success rate (last 24h) and latency. healthy=100, degraded=60, offline=0. Mutaba'aa anomaly scanner consumes this when ranking incidents.`,
                            ),
                          )
                        }
                      >
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-20 rounded-full bg-surface-sunken">
                            <div
                              className={`h-2 rounded-full ${_healthBarColor(row.health_score_pct)}`}
                              style={{ width: `${row.health_score_pct}%` }}
                            />
                          </div>
                          <span className="font-mono text-xs">{row.health_score_pct}%</span>
                        </div>
                      </TraceableValue>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t("attendance.biometric_sync.title", "Biometric Sync"),
            window_label: `${counts.healthy} healthy · ${counts.degraded} degraded · ${counts.offline} offline`,
          }}
        />
        <div className="border-t border-border-subtle p-3 text-[10px] text-text-tertiary">
          <ActivitySquare className="me-1 inline h-3 w-3" />
          {t("attendance.biometric_sync.note", "Health scores refresh every 30s.")}
        </div>
      </aside>
    </div>
  );
}
